import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Search, 
  RefreshCw, 
  Eye, 
  Mail, 
  Phone, 
  Building2,
  Sparkles,
  Filter,
  X,
  Save,
  LayoutGrid,
  Download,
  ClipboardList,
  Trophy
} from "lucide-react";
import AddLeadDialog from "@/components/AddLeadDialog";
import ExportLeadsDialog from "@/components/ExportLeadsDialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadsTableEnhanced = ({ onViewKanban }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [selectAllAcrossPages, setSelectAllAcrossPages] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [pendingTasksCount, setPendingTasksCount] = useState(0);
  const [wonLeadsCount, setWonLeadsCount] = useState(0);
  const [filters, setFilters] = useState({
    stage: "",
    enrichment_status: "",
    verify_email: "",
    verify_phone: "",
    min_score: "",
    max_score: "",
    owner_id: "",
    lead_date_from: "",
    lead_date_to: ""
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchLeads();
    fetchTasks();
    const interval = setInterval(() => {
      fetchLeads();
      fetchTasks();
    }, 5000);
    return () => clearInterval(interval);
  }, [filters, search]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        withCredentials: true
      });
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchLeads = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filters.stage) params.append('stage', filters.stage);
      if (filters.enrichment_status) params.append('enrichment_status', filters.enrichment_status);
      if (filters.verify_email) params.append('verify_email', filters.verify_email);
      if (filters.verify_phone) params.append('verify_phone', filters.verify_phone);
      if (filters.min_score) params.append('min_score', filters.min_score);
      if (filters.max_score) params.append('max_score', filters.max_score);
      if (filters.owner_id) params.append('owner_id', filters.owner_id);
      if (filters.lead_date_from) params.append('lead_date_from', filters.lead_date_from);
      if (filters.lead_date_to) params.append('lead_date_to', filters.lead_date_to);

      const response = await axios.get(`${API}/leads?${params.toString()}`, {
        withCredentials: true
      });
      setLeads(response.data);
      setTotalCount(response.data.length);
      
      // Calculate won leads count
      const wonCount = response.data.filter(lead => 
        lead.stage && lead.stage.toLowerCase() === 'won'
      ).length;
      setWonLeadsCount(wonCount);
      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching leads:", error);
      toast.error("Failed to fetch leads");
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks`, {
        withCredentials: true
      });
      // Count pending tasks (status !== 'completed')
      const pendingCount = response.data.filter(task => 
        task.status !== 'completed'
      ).length;
      setPendingTasksCount(pendingCount);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
  };

  const updateLeadStage = async (leadId, newStage) => {
    try {
      await axios.patch(`${API}/leads/${leadId}`, { stage: newStage }, {
        withCredentials: true
      });
      toast.success("Stage updated");
      fetchLeads();
    } catch (error) {
      toast.error("Failed to update stage");
    }
  };

  const updateLeadOwner = async (leadId, ownerId) => {
    try {
      const ownerValue = ownerId === "unassigned" ? null : ownerId;
      await axios.patch(`${API}/leads/${leadId}`, { owner_id: ownerValue }, {
        withCredentials: true
      });
      toast.success(ownerValue ? "Owner assigned" : "Owner removed");
      fetchLeads();
    } catch (error) {
      toast.error("Failed to update owner");
    }
  };

  const clearFilters = () => {
    setFilters({
      stage: "",
      enrichment_status: "",
      verify_email: "",
      verify_phone: "",
      min_score: "",
      max_score: "",
      owner_id: "",
      lead_date_from: "",
      lead_date_to: ""
    });
    setSearch("");
    // Clear selection when filters change
    setSelectedLeads(new Set());
    setSelectAll(false);
    setSelectAllAcrossPages(false);
  };

  // Selection handlers
  const handleSelectLead = (leadId) => {
    const newSelection = new Set(selectedLeads);
    if (newSelection.has(leadId)) {
      newSelection.delete(leadId);
    } else {
      newSelection.add(leadId);
    }
    setSelectedLeads(newSelection);
    setSelectAll(newSelection.size === leads.length);
  };

  const handleSelectAllOnPage = (checked) => {
    if (checked) {
      const newSelection = new Set(leads.map(lead => lead.id));
      setSelectedLeads(newSelection);
      setSelectAll(true);
    } else {
      setSelectedLeads(new Set());
      setSelectAll(false);
      setSelectAllAcrossPages(false);
    }
  };

  const handleSelectAllAcrossPages = () => {
    setSelectAllAcrossPages(true);
    setSelectAll(true);
    toast.info(`All ${totalCount} filtered leads selected for export`);
  };

  const handleExport = () => {
    if (selectAllAcrossPages) {
      // Export all filtered leads
      setShowExportDialog(true);
    } else if (selectedLeads.size > 0) {
      // Export selected leads
      setShowExportDialog(true);
    } else {
      toast.error("Please select leads to export");
    }
  };

  const isAdmin = user?.role === 'admin';
  const canExport = user?.role === 'admin' || user?.is_export_user;

  const getVerificationBadge = (status) => {
    const styles = {
      valid: "bg-emerald-100 text-emerald-700 hover:bg-emerald-200",
      invalid: "bg-red-100 text-red-700 hover:bg-red-200",
      risky: "bg-amber-100 text-amber-700 hover:bg-amber-200",
      unknown: "bg-slate-100 text-slate-600 hover:bg-slate-200"
    };
    return (
      <Badge className={styles[status] || styles.unknown}>
        {status}
      </Badge>
    );
  };

  const getEnrichmentBadge = (status) => {
    const styles = {
      enriched: "bg-green-100 text-green-700 hover:bg-green-200",
      pending: "bg-blue-100 text-blue-700 hover:bg-blue-200",
      failed: "bg-red-100 text-red-700 hover:bg-red-200"
    };
    return (
      <Badge className={styles[status] || styles.pending}>
        {status === 'enriched' && <Sparkles className="w-3 h-3 mr-1" />}
        {status}
      </Badge>
    );
  };

  const getStageBadge = (stage) => {
    const styles = {
      new: "bg-blue-100 text-blue-700",
      contacted: "bg-purple-100 text-purple-700",
      qualified: "bg-green-100 text-green-700",
      negotiating: "bg-amber-100 text-amber-700",
      won: "bg-emerald-100 text-emerald-700",
      lost: "bg-red-100 text-red-700"
    };
    return (
      <Badge className={styles[stage] || styles.new}>
        {stage}
      </Badge>
    );
  };

  const getScoreColor = (score) => {
    if (score >= 70) return "score-high";
    if (score >= 40) return "score-medium";
    return "score-low";
  };

  const activeFiltersCount = Object.values(filters).filter(v => v !== "").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="loading-leads">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600 text-lg">Loading leads...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="leads-table-container">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Leads</h2>
          <p className="text-sm sm:text-base text-slate-600 mt-1">Manage and enrich your lead database</p>
        </div>
        <div className="flex items-center space-x-2 flex-wrap">
          <AddLeadDialog onLeadAdded={fetchLeads} />
          {canExport && (
            <Button 
              onClick={handleExport}
              variant="default"
              className="flex items-center space-x-2 bg-indigo-600 hover:bg-indigo-700"
              data-testid="export-leads-btn"
              disabled={!selectAllAcrossPages && selectedLeads.size === 0}
            >
              <Download className="w-4 h-4" />
              <span>Export CSV</span>
              {(selectAllAcrossPages || selectedLeads.size > 0) && (
                <Badge className="ml-2 bg-white text-indigo-600">
                  {selectAllAcrossPages ? totalCount : selectedLeads.size}
                </Badge>
              )}
            </Button>
          )}
          <Button 
            onClick={onViewKanban}
            variant="outline"
            className="flex items-center space-x-2"
            data-testid="view-kanban-btn"
          >
            <LayoutGrid className="w-4 h-4" />
            <span className="hidden sm:inline">Kanban View</span>
          </Button>
          <Button 
            onClick={fetchLeads}
            variant="outline"
            className="flex items-center space-x-2"
            data-testid="refresh-leads-btn"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search by name, email, or company..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-12 h-12 bg-white/80 backdrop-blur-sm border-slate-200 focus:border-indigo-500"
              data-testid="search-leads-input"
            />
          </div>
          <Button
            onClick={() => setShowFilters(!showFilters)}
            variant={showFilters || activeFiltersCount > 0 ? "default" : "outline"}
            className={`flex items-center space-x-2 h-12 ${
              showFilters || activeFiltersCount > 0
                ? "bg-gradient-to-r from-indigo-600 to-blue-600"
                : ""
            }`}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4" />
            <span className="hidden sm:inline">Filters</span>
            {activeFiltersCount > 0 && (
              <Badge className="bg-white text-indigo-600 ml-1">{activeFiltersCount}</Badge>
            )}
          </Button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="glass-card rounded-xl p-6 space-y-4" data-testid="filters-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">Filters</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-slate-600"
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4 mr-1" />
                Clear All
              </Button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Stage</label>
                <Select value={filters.stage} onValueChange={(value) => setFilters({...filters, stage: value === "all" ? "" : value})}>
                  <SelectTrigger data-testid="filter-stage">
                    <SelectValue placeholder="All Stages" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Stages</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="negotiating">Negotiating</SelectItem>
                    <SelectItem value="won">Won</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Enrichment Status</label>
                <Select value={filters.enrichment_status} onValueChange={(value) => setFilters({...filters, enrichment_status: value === "all" ? "" : value})}>
                  <SelectTrigger data-testid="filter-enrichment">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="enriched">Enriched</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Email Verification</label>
                <Select value={filters.verify_email} onValueChange={(value) => setFilters({...filters, verify_email: value === "all" ? "" : value})}>
                  <SelectTrigger data-testid="filter-email">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="valid">Valid</SelectItem>
                    <SelectItem value="invalid">Invalid</SelectItem>
                    <SelectItem value="risky">Risky</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Phone Verification</label>
                <Select value={filters.verify_phone} onValueChange={(value) => setFilters({...filters, verify_phone: value === "all" ? "" : value})}>
                  <SelectTrigger data-testid="filter-phone">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="valid">Valid</SelectItem>
                    <SelectItem value="invalid">Invalid</SelectItem>
                    <SelectItem value="risky">Risky</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Lead Owner</label>
                <Select value={filters.owner_id || "all"} onValueChange={(value) => setFilters({...filters, owner_id: value === "all" ? "" : value})}>
                  <SelectTrigger data-testid="filter-owner">
                    <SelectValue placeholder="All Owners" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Owners</SelectItem>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.first_name && user.last_name 
                          ? `${user.first_name} ${user.last_name}` 
                          : user.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Min Score</label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={filters.min_score}
                    onChange={(e) => setFilters({...filters, min_score: e.target.value})}
                    min="0"
                    max="100"
                    data-testid="filter-min-score"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Max Score</label>
                  <Input
                    type="number"
                    placeholder="100"
                    value={filters.max_score}
                    onChange={(e) => setFilters({...filters, max_score: e.target.value})}
                    min="0"
                    max="100"
                    data-testid="filter-max-score"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Lead Date From</label>
                  <Input
                    type="date"
                    value={filters.lead_date_from}
                    onChange={(e) => setFilters({...filters, lead_date_from: e.target.value})}
                    data-testid="filter-lead-date-from"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">Lead Date To</label>
                  <Input
                    type="date"
                    value={filters.lead_date_to}
                    onChange={(e) => setFilters({...filters, lead_date_to: e.target.value})}
                    data-testid="filter-lead-date-to"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="glass-card rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 font-medium">Total Leads</p>
              <p className="text-3xl font-bold text-slate-900 mt-1">{leads.length}</p>
            </div>
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-indigo-600" />
            </div>
          </div>
        </div>
        <div className="glass-card rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 font-medium">Enriched</p>
              <p className="text-3xl font-bold text-green-600 mt-1">
                {leads.filter(l => l.enrichment_status === 'enriched').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        <div className="glass-card rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 font-medium">Total Pending Tasks</p>
              <p className="text-3xl font-bold text-amber-600 mt-1">
                {pendingTasksCount}
              </p>
            </div>
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
              <ClipboardList className="w-6 h-6 text-amber-600" />
            </div>
          </div>
        </div>
        <div className="glass-card rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600 font-medium">Total Won</p>
              <p className="text-3xl font-bold text-green-600 mt-1">
                {wonLeadsCount}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Trophy className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      {leads.length === 0 ? (
        <div className="glass-card rounded-xl p-12 text-center" data-testid="empty-leads-state">
          <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-900 mb-2">No leads found</h3>
          <p className="text-slate-600 mb-6">Import a CSV file or use the chat to add leads</p>
          <Button 
            onClick={() => navigate('/import')}
            className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
            data-testid="go-to-import-btn"
          >
            Import Leads
          </Button>
        </div>
      ) : (
        <>
          {/* Selection Banner */}
          {isAdmin && selectAll && !selectAllAcrossPages && totalCount > leads.length && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-900">
                  <strong>{leads.length}</strong> leads on this page are selected.
                </p>
              </div>
              <Button
                variant="link"
                onClick={handleSelectAllAcrossPages}
                className="text-blue-700 hover:text-blue-900 font-semibold"
              >
                Select all {totalCount} filtered leads
              </Button>
            </div>
          )}
          
          {isAdmin && selectAllAcrossPages && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-indigo-900">
                  <strong>All {totalCount} filtered leads</strong> are selected for export.
                </p>
              </div>
              <Button
                variant="link"
                onClick={() => {
                  setSelectAllAcrossPages(false);
                  setSelectedLeads(new Set());
                  setSelectAll(false);
                }}
                className="text-indigo-700 hover:text-indigo-900 font-semibold"
              >
                Clear selection
              </Button>
            </div>
          )}

          <div className="glass-card rounded-xl overflow-hidden" data-testid="leads-table">
            <div className="overflow-x-auto">
            <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/50">
                {isAdmin && (
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectAll}
                      onCheckedChange={handleSelectAllOnPage}
                      aria-label="Select all leads on this page"
                    />
                  </TableHead>
                )}
                <TableHead className="font-semibold text-slate-700">Name</TableHead>
                <TableHead className="font-semibold text-slate-700 hidden md:table-cell">Company</TableHead>
                <TableHead className="font-semibold text-slate-700 hidden lg:table-cell">Lead Date</TableHead>
                <TableHead className="font-semibold text-slate-700 hidden xl:table-cell">Owner</TableHead>
                <TableHead className="font-semibold text-slate-700">Stage</TableHead>
                <TableHead className="font-semibold text-slate-700 hidden sm:table-cell">Status</TableHead>
                <TableHead className="font-semibold text-slate-700 hidden lg:table-cell">Score</TableHead>
                <TableHead className="font-semibold text-slate-700 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.map((lead) => (
                <TableRow 
                  key={lead.id} 
                  className="hover:bg-slate-50/50 transition-colors"
                  data-testid={`lead-row-${lead.id}`}
                >
                  {isAdmin && (
                    <TableCell className="w-12">
                      <Checkbox
                        checked={selectedLeads.has(lead.id) || selectAllAcrossPages}
                        onCheckedChange={() => handleSelectLead(lead.id)}
                        disabled={selectAllAcrossPages}
                        aria-label={`Select lead ${lead.raw_data?.name}`}
                      />
                    </TableCell>
                  )}
                  <TableCell className="font-medium text-slate-900">
                    <button
                      onClick={() => navigate(`/leads/${lead.id}`)}
                      className="text-left hover:text-blue-600 hover:underline transition-colors cursor-pointer"
                      data-testid={`lead-name-link-${lead.id}`}
                    >
                      {lead.raw_data?.name || 'N/A'}
                    </button>
                    <div className="md:hidden text-xs text-slate-500 mt-1">
                      {lead.raw_data?.company || 'N/A'}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-600 hidden md:table-cell">
                    {lead.raw_data?.company || 'N/A'}
                  </TableCell>
                  <TableCell className="text-slate-600 hidden lg:table-cell">
                    {lead.lead_date || '—'}
                  </TableCell>
                  <TableCell className="hidden xl:table-cell">
                    <Select
                      value={lead.owner_id || "unassigned"}
                      onValueChange={(value) => updateLeadOwner(lead.id, value)}
                    >
                      <SelectTrigger className="w-40 h-8 text-sm">
                        <SelectValue>
                          {lead.owner_id ? (
                            (() => {
                              const owner = users.find(u => u.id === lead.owner_id);
                              if (owner) {
                                return owner.first_name && owner.last_name 
                                  ? `${owner.first_name} ${owner.last_name}` 
                                  : owner.email;
                              }
                              return "Unknown";
                            })()
                          ) : (
                            <span className="text-slate-400">Unassigned</span>
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {users.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.first_name && user.last_name 
                              ? `${user.first_name} ${user.last_name}` 
                              : user.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={lead.stage}
                      onValueChange={(value) => updateLeadStage(lead.id, value)}
                    >
                      <SelectTrigger className="w-32 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="qualified">Qualified</SelectItem>
                        <SelectItem value="negotiating">Negotiating</SelectItem>
                        <SelectItem value="won">Won</SelectItem>
                        <SelectItem value="lost">Lost</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    {getEnrichmentBadge(lead.enrichment_status)}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <div className="flex items-center gap-2">
                      {(() => {
                        // Try to get qualification score from enriched data first
                        const qualificationScore = lead.enriched_data?.lead_qualification?.qualification_score;
                        const displayScore = qualificationScore !== undefined && qualificationScore !== null 
                          ? qualificationScore 
                          : lead.score_rule;
                        
                        if (displayScore !== undefined && displayScore !== null) {
                          return (
                            <Badge 
                              variant="outline"
                              className={`
                                ${displayScore >= 71 ? 'bg-green-50 text-green-700 border-green-300' : ''}
                                ${displayScore >= 41 && displayScore <= 70 ? 'bg-yellow-50 text-yellow-700 border-yellow-300' : ''}
                                ${displayScore <= 40 ? 'bg-red-50 text-red-700 border-red-300' : ''}
                              `}
                              title={qualificationScore !== undefined ? "AI Qualification Score" : "Rule-based Score"}
                            >
                              {Math.round(displayScore)}
                            </Badge>
                          );
                        } else {
                          return <span className="text-slate-400 text-sm">N/A</span>;
                        }
                      })()}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end space-x-1">
                      {lead.raw_data?.email && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 hidden sm:inline-flex"
                          onClick={() => window.location.href = `mailto:${lead.raw_data.email}`}
                          title="Send Email"
                        >
                          <Mail className="w-4 h-4" />
                        </Button>
                      )}
                      {lead.raw_data?.phone && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 hidden sm:inline-flex"
                          onClick={() => window.location.href = `tel:${lead.raw_data.phone}`}
                          title="Call"
                        >
                          <Phone className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8"
                        onClick={() => navigate(`/leads/${lead.id}`)}
                        data-testid={`view-lead-${lead.id}`}
                      >
                        View
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          </div>
        </>
      )}

      {/* Export Dialog */}
      {isAdmin && (
        <ExportLeadsDialog
          open={showExportDialog}
          onClose={() => setShowExportDialog(false)}
          selectedIds={selectAllAcrossPages ? [] : Array.from(selectedLeads)}
          filters={filters}
          totalFilteredCount={totalCount}
        />
      )}
    </div>
  );
};

export default LeadsTableEnhanced;
